import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks';
import { useFileAssociation } from '@/lib/crm/hooks/useFileAssociation';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { getObject } from '@/lib/crm/store/crmObjectStore';
import { submitEventsOptimistic } from '@/lib/crm/store/submitEventsOptimistic';
import { attachWithRollback } from '@/lib/files/attachWithRollback';
import { fileService } from '@/lib/files/service';
import { logger } from '@/lib/logger';
import type { CrmObject, EntityTypeName } from '@/types/crm';

import { useRecordAttachmentDownloads } from './useRecordAttachmentDownloads';

// File picking is NOT handled here: the view renders a persistent hidden
// input via `useFilePicker` (@/lib/base/file-upload) and feeds the chosen file
// to this hook's `handleFile`. An earlier revision created a hidden input
// imperatively inside the gesture and `.click()`ed it; that silently failed to
// open the chooser in real Chrome (verified by field console logs: click
// invoked on an in-document input, yet no `change`/`cancel` and no activation
// warning) while passing under Playwright, whose CDP layer intercepts choosers
// before the real dialog would open. See useFilePicker's doc comment.

// Upload/attach, detach, and download-delegation stay co-located in this one
// hook (rather than three separate hooks) because all three share `orgId`,
// `object`, and `invalidate` context. Splitting them would push three
// separate error states and three separate `invalidate()` calls onto every
// caller, in exchange for a file that's already broken into distinct,
// clearly-bounded sections below.
export function useRecordAttachments(
  object: CrmObject | undefined,
  orgId: number,
  entityTypeName: string
) {
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();

  const { association } = useFileAssociation(orgId, entityTypeName);
  const reverseType = association?.reverse;

  const fileEntityIds = useMemo(() => {
    if (!object || !reverseType) {
      return [];
    }
    return (object.associations ?? [])
      .filter(a => a.association_type_name === reverseType)
      .map(a => a.target_entity_id)
      .filter((id): id is string => Boolean(id));
  }, [object, reverseType]);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [detachError, setDetachError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const submittingIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Reset to true on every effect setup, not just the initial mount. React
    // Strict Mode mounts → cleans up → mounts again; without re-setting here
    // the first cleanup would leave the ref permanently false, so the `finally`
    // blocks below would skip their `setIsUploading(false)`/`setIsSubmitting(false)`
    // and strand the Add action on "Uploading…" and rows non-removable.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /** Refresh both the record's list query AND the slideout's focused-object
   *  query after a mutation — see NoteLinkedRecordsSection's `invalidate`
   *  docstring for why both are needed and why callers must await it. */
  const invalidate = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(
            orgId,
            entityTypeName as EntityTypeName
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: ['crm', orgId, 'object', object?.entity_id],
        }),
      ]),
    [queryClient, orgId, entityTypeName, object?.entity_id]
  );

  const handleFile = useCallback(
    async (file: File) => {
      if (!object) {
        return;
      }

      const assocType = association?.forward;
      if (!assocType) {
        setUploadError('Files are not supported for this record type.');
        return;
      }

      setIsUploading(true);
      setUploadError(null);
      try {
        const uploaded = await fileService.uploadFile(orgId, file);
        await attachWithRollback({
          orgId,
          uploadedFileId: uploaded.id,
          entityId: object.entity_id,
          logComponent: 'record-attachments',
          attach: () =>
            fileService.attachToRecord(
              orgId,
              uploaded.id,
              object.entity_id,
              assocType
            ),
        });
        await invalidate();
      } catch (error) {
        logger.error(
          'RecordAttachments: failed to upload/attach file',
          error instanceof Error ? error : undefined,
          { component: 'record-attachments', orgId, entityId: object.entity_id }
        );
        setUploadError("We couldn't attach the file. Try again.");
      } finally {
        if (mountedRef.current) {
          setIsUploading(false);
        }
      }
    },
    [orgId, object, invalidate, association]
  );

  /** Clear a stale upload error the moment the user re-opens the picker, so
   *  the old message doesn't linger under the section while the (page-blocking)
   *  native chooser is up. `handleFile` clears it again on an actual pick. */
  const clearUploadError = useCallback(() => {
    setUploadError(null);
  }, []);

  const handleRemove = useCallback(
    async (fileEntityId: string) => {
      const entityId = object?.entity_id;
      if (!entityId || !user?.id || !reverseType) {
        return;
      }
      // Block detach while an upload/attach is in flight. The attach mutates
      // the record's associations and bumps its seq, but this detach captures
      // the pre-attach seq — submitting now would race the attach on a stale
      // sequence. The Add/remove affordances are also disabled on
      // `isUploading` in the view; this is the hook-level backstop for that
      // gate.
      if (isUploading) {
        logger.warn('RecordAttachments: blocked detach during upload', {
          component: 'record-attachments',
          orgId,
          entityId,
          fileEntityId,
        });
        return;
      }
      if (submittingIdsRef.current.has(fileEntityId)) {
        logger.warn('RecordAttachments: blocked concurrent detach', {
          component: 'record-attachments',
          orgId,
          entityId,
          fileEntityId,
        });
        return;
      }
      submittingIdsRef.current.add(fileEntityId);
      setIsSubmitting(true);
      setDetachError(null);
      try {
        // Read the latest seq from the canonical store at submit time rather
        // than closing over the render-time `object` — that keeps this
        // callback's identity stable across unrelated WebSocket record
        // updates (see the useCallback deps below) while still submitting
        // against a fresh sequence.
        const fallbackSeq = getObject(entityId)?.last_event_seq;
        await submitEventsOptimistic({
          orgId,
          userId: user.id,
          entityTypeName: entityTypeName as EntityTypeName,
          primaryEntityId: entityId,
          fallbackSeq,
          build: builder => {
            builder.associationRemoved(
              entityId,
              entityTypeName,
              reverseType,
              fileEntityId
            );
          },
        });
        await invalidate();
      } catch (error) {
        logger.error(
          'RecordAttachments: failed to detach file',
          error instanceof Error ? error : undefined,
          { org_id: orgId, entity_id: entityId }
        );
        setDetachError("We couldn't detach the file. Try again.");
      } finally {
        submittingIdsRef.current.delete(fileEntityId);
        if (mountedRef.current) {
          setIsSubmitting(false);
        }
      }
    },
    [
      user?.id,
      object?.entity_id,
      orgId,
      entityTypeName,
      reverseType,
      invalidate,
      isUploading,
    ]
  );

  const { canDownloadAll, handleDownload, handleDownloadAll } =
    useRecordAttachmentDownloads(fileEntityIds, orgId);

  return {
    fileEntityIds,
    isUploading,
    isSubmitting,
    uploadError,
    detachError,
    canDownloadAll,
    handleFile,
    clearUploadError,
    handleRemove,
    handleDownload,
    handleDownloadAll,
  };
}
